/**
 * Confirm — last step before payment. Shows the team + division +
 * fee preview. Clicking "Submit" creates the TeamRegistration server-
 * side and routes to the payment page.
 *
 * If the event uses APPROVAL_REQUIRED mode, the registration goes to
 * pending_approval status server-side; we route to a pending state in
 * that case rather than payment. (Approval flow UI lands later — for
 * PR C we just route to /done with a pending-state summary.)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  EmbedClient, AvailableEvent, BulkOffer, CoachProfile, AddonProduct, QuoteResponse,
  RegistrationQuestion,
} from '@/api/embed';
import { SaveTeamOption } from '@/components/SaveTeamOption';
import { useFeature } from '@/flags';
import { useFlow } from '@/state/FlowContext';
import { useConfig, useVocab } from '@/state/vocab';
import { offerDiscount, offerLabel } from './bulkOffers';
import '@/pages/ConfirmPage.css';

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Coach-facing label for an applied discount. Prefers the organizer's own
 * promotion name ("Returning team 10% off") and falls back to a friendly
 * label per type so a coupon/loyalty/bulk discount always reads clearly.
 */
function discountLabel(type: string, name: string | null): string {
  if (name && name.trim()) return name.trim();
  switch (type) {
    case 'COUPON': return 'Coupon discount';
    case 'LOYALTY': return 'Loyalty reward';
    case 'BULK': return 'Multi-team discount';
    default: return 'Discount';
  }
}

interface Props {
  client: EmbedClient;
}

export function GfgConfirmPage({ client }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const {
    selectedTeam, selectedDivision, setRegistration,
    createAccount, setCreateAccount,
    addonQuantities, setAddonQuantity, startBulkFrom,
  } = useFlow();
  const vocab = useVocab();
  const config = useConfig();
  const upsellOn = useFeature('bulk_upsell') && !vocab.isCharity;
  // Questions are a TourneyNation feature; Golf Fore Good and charity checkouts stay as they were.
  const asksQuestions = config?.theme !== 'gfg' && !vocab.isCharity;

  const [event, setEvent] = useState<AvailableEvent | null>(null);
  // How many other events this team could still enter here, and the
  // organizer's multi-event tiers: together they decide the upsell.
  const [otherEvents, setOtherEvents] = useState(0);
  const [offers, setOffers] = useState<BulkOffer[]>([]);
  const [me, setMe] = useState<CoachProfile | null>(null);
  const [addons, setAddons] = useState<AddonProduct[]>([]);
  // Division-resolved fee + any auto discount/credit. Drives the fee display
  // so the summary matches what payment charges (event.fee_cents is only the
  // event default and misses per-division pricing). Null until loaded / on
  // failure — we fall back to the event default fee then.
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  // Which payment method is mid-submit ('card' | 'offline'), so only the
  // clicked button shows a busy label. null = idle.
  const [submitting, setSubmitting] = useState<null | 'card' | 'offline'>(null);
  const busy = submitting !== null;
  const [error, setError] = useState<string | null>(null);
  // The organizer's questions, answered here before payment.
  const [questions, setQuestions] = useState<RegistrationQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [qErrs, setQErrs] = useState<Record<string, string>>({});
  const [qError, setQError] = useState<string | null>(null);

  // Add-ons are exposed for charity events only. Backend returns [] for
  // club events, but we skip the call entirely to save a round trip.
  useEffect(() => {
    if (!eventId || !vocab.isCharity) return;
    client.getAddons(eventId).then(setAddons).catch(() => { /* non-fatal */ });
  }, [client, eventId, vocab.isCharity]);

  const addonSubtotalCents = addons.reduce(
    (sum, p) => sum + p.price_cents * (addonQuantities[p.id] ?? 0),
    0,
  );

  useEffect(() => {
    if (!selectedTeam) {
      navigate(`/events/${eventId}/teams`, { replace: true });
      return;
    }
    client
      .getAvailableEvents(selectedTeam.id)
      .then((events) => {
        const found = events.find((e) => e.id === eventId) ?? null;
        if (!found) {
          // Filtered server-side: registration closed by the organizer,
          // event no longer live, or this team already registered.
          setError('This event is no longer open for registration.');
          return;
        }
        setEvent(found);
        setOtherEvents(events.filter((e) => e.id !== eventId).length);
      })
      .catch(() => {
        setError('Could not load event details.');
      });
    // Read account_status to decide whether to show the create-account
    // checkbox. Failures fall through to "real" — we just don't offer
    // the prompt; coach can sign up via the main app later.
    client.getMe().then(setMe).catch(() => { /* non-fatal */ });
  }, [client, eventId, selectedTeam, navigate]);

  useEffect(() => {
    if (!upsellOn || !selectedTeam) return;
    client.getBulkOffers().then(setOffers).catch(() => setOffers([]));
  }, [client, upsellOn, selectedTeam]);

  // Price preview: real division fee + any auto discount/credit. Non-fatal —
  // on failure we render the event default fee (better a slightly stale
  // number than a blocked screen), and the register call is authoritative.
  useEffect(() => {
    if (!selectedTeam || !eventId) return;
    let cancelled = false;
    client
      .getQuote(selectedTeam.id, eventId, selectedDivision ?? undefined)
      .then((q) => { if (!cancelled) setQuote(q); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [client, eventId, selectedTeam, selectedDivision]);

  useEffect(() => {
    if (!eventId || !asksQuestions) return;
    client.getRegistrationQuestions(eventId).then(setQuestions).catch(() => { /* the server still checks */ });
  }, [client, eventId, asksQuestions]);

  if (!selectedTeam) return null;

  if (error) {
    return <div role="alert" className="tn-confirm__error">{error}</div>;
  }

  if (!event) {
    return <div className="tn-confirm__loading">Loading…</div>;
  }

  const onSubmit = async (method: 'card' | 'offline' = 'card') => {
    const errs = checkAnswers(questions, answers);
    setQErrs(errs);
    setQError(null);
    if (Object.keys(errs).length) {
      document.getElementById(`tn-rq-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setSubmitting(method);
    setError(null);
    try {
      // Offline pays in a single step (no create-payment-intent), so the
      // add-ons chosen above ride on the register call. The card flow folds
      // them into the PaymentIntent on the payment page instead.
      const addonItems = method === 'offline'
        ? addons
            .map((p) => ({ product_id: p.id, quantity: addonQuantities[p.id] ?? 0 }))
            .filter((i) => i.quantity > 0)
        : undefined;
      const registration = await client.registerTeam(
        selectedTeam.id,
        eventId!,
        selectedDivision ?? undefined,
        method === 'offline' ? { paymentMethod: 'offline', addonItems, answers } : { answers },
      );
      setRegistration(registration);
      if (registration.status === 'pending_approval') {
        navigate(`/events/${eventId}/done?awaiting_approval=1`);
      } else if (method === 'offline') {
        navigate(`/events/${eventId}/offline`);
      } else {
        navigate(`/events/${eventId}/payment`);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (questions.length > 0 && err?.response?.status === 422 && typeof detail === 'string') {
        // A question the server didn't accept: show it by the questions, keep the page.
        setQError(detail);
      } else if (detail?.code === 'ALREADY_REGISTERED') {
        setError(
          detail.message
            ?? `This ${vocab.entrant} is already registered for this event. Pick a different ${vocab.entrant}.`,
        );
      } else if (detail?.code === 'PAYMENT_PROCESSING') {
        setError(detail.message ?? 'Your earlier payment is still processing. Check back in a minute.');
      } else if (detail?.code === 'REGISTRATION_CLOSED') {
        // Organizer closed registration (mode or window) mid-flow.
        setError('Registration for this event has closed.');
      } else if (detail?.code === 'DIVISION_FULL') {
        setError(`This ${vocab.division} is full. Go back and pick a different ${vocab.division}.`);
      } else if (detail?.code === 'REGISTRATION_NOT_OPEN') {
        setError('Registration hasn\'t opened yet for this event.');
      } else {
        setError('Could not submit your registration. Please try again.');
      }
    } finally {
      setSubmitting(null);
    }
  };

  // Gross fee: the quote's division-resolved fee when available, else the
  // event default. netFee is what's actually charged after auto discount +
  // credit; the difference is broken out below so the coach sees why.
  const grossFee = quote ? quote.fee_cents : event.fee_cents;
  const netFee = quote ? quote.net_cents : event.fee_cents;
  const discountCents = quote ? quote.discount_cents : 0;
  const creditCents = quote ? quote.credit_cents : 0;
  const requiresApproval = event.registration_mode === 'approval_required';
  // Offer the offline choice only when the organizer enabled it, there's an
  // amount to actually pay (fee + add-ons), and we're not in approval mode
  // (approval events collect payment after approval, outside this widget).
  const payableCents = netFee + addonSubtotalCents;
  const offlineAvailable =
    !!event.offline_payment_enabled && !requiresApproval && payableCents > 0;


  // Upsell into bulk: the lowest tier this team can reach with its other
  // open events, and only when it beats the discount it already has here.
  const upsell = upsellOn && quote
    ? offers
        .filter((o) => o.min_events <= otherEvents + 1)
        .filter((o) => offerDiscount(o, grossFee) > discountCents)
        .sort((a, b) => a.min_events - b.min_events)[0] ?? null
    : null;
  const organizer = config?.branding.organizer_name?.trim();

  return (
    <div className="tn-confirm">
      <h1 className="tn-confirm__title">Register {selectedTeam.name}</h1>
      <p className="tn-confirm__subtitle">
        Confirm {vocab.entrant} details before {requiresApproval ? 'submitting' : 'paying'}.
      </p>

      {requiresApproval ? (
        <div className="tn-confirm__banner">
          <div>
            <strong>Approval required</strong>
            <div className="tn-confirm__banner-body">
              The organizer must approve this registration before payment.
              You'll receive an email when they decide.
            </div>
          </div>
        </div>
      ) : null}

      <div className="tn-card tn-confirm__card">
        <div className="tn-confirm__label">Event</div>
        <div className="tn-confirm__name">{event.name}</div>
        <div className="tn-confirm__meta">
          {event.event_date
            ? new Date(event.event_date).toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'Date TBD'}
          {event.location ? ` · ${event.location}` : ''}
        </div>
        {grossFee > 0 ? (
          <div className="tn-confirm__pricing">
            <div className="tn-confirm__price-row">
              <span>Registration fee</span>
              <span
                className={
                  discountCents > 0 || creditCents > 0
                    ? 'tn-confirm__price-struck'
                    : 'tn-confirm__money'
                }
              >
                {money(grossFee)}
              </span>
            </div>

            {/* Explain the discount so the coach understands the lower price. */}
            {quote?.discounts.map((d, i) => (
              <div className="tn-confirm__price-row tn-confirm__price-row--discount" key={i}>
                <span>{discountLabel(d.type, d.name)}</span>
                <span>−{money(d.discount_cents)}</span>
              </div>
            ))}
            {/* Fallback: discount present but no itemized labels came back. */}
            {discountCents > 0 && (quote?.discounts.length ?? 0) === 0 ? (
              <div className="tn-confirm__price-row tn-confirm__price-row--discount">
                <span>Discount applied</span>
                <span>−{money(discountCents)}</span>
              </div>
            ) : null}

            {creditCents > 0 ? (
              <div className="tn-confirm__price-row tn-confirm__price-row--discount">
                <span>Account credit</span>
                <span>−{money(creditCents)}</span>
              </div>
            ) : null}

            {discountCents > 0 || creditCents > 0 ? (
              <div className="tn-confirm__price-row tn-confirm__price-row--total">
                <span>Registration fee after discounts</span>
                <span className="tn-confirm__money">{money(netFee)}</span>
              </div>
            ) : null}
            {/* Not a total: paying by card adds the platform and processing
                fees (and any add-ons), itemized on the payment step before the charge. */}
            {netFee > 0 ? (
              <div className="tn-addons__note">
                Card payments may add a platform fee and a processing fee, shown itemized before you pay.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="tn-confirm__fee tn-confirm__fee--free">
            No registration fee
          </div>
        )}
      </div>

      <div className="tn-card tn-confirm__card">
        <div className="tn-confirm__label">{vocab.entrantCap}</div>
        <div className="tn-confirm__name">{selectedTeam.name}</div>
        <div className="tn-confirm__meta">
          {[selectedTeam.sport, selectedTeam.age_group, selectedTeam.gender]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {/* Roster is a sports concept — a charity "entry" has no players. */}
        {!vocab.isCharity ? (
          <div className="tn-confirm__meta">
            {selectedTeam.roster_count} players on roster
          </div>
        ) : null}
      </div>

      {selectedDivision ? (
        <div className="tn-card tn-confirm__card">
          <div className="tn-confirm__label">{vocab.divisionCap}</div>
          <div className="tn-confirm__name">{selectedDivision}</div>
        </div>
      ) : null}

      {questions.length > 0 ? (
        <div className="tn-card tn-confirm__card tn-rq">
          <div className="tn-confirm__label">Questions from the organizer</div>
          {questions.map((q) => (
            <QuestionField
              key={q.id}
              q={q}
              value={answers[q.id] ?? ''}
              error={qErrs[q.id]}
              onChange={(v) => {
                setAnswers({ ...answers, [q.id]: v });
                if (qErrs[q.id]) setQErrs({ ...qErrs, [q.id]: '' });
              }}
            />
          ))}
          {qError ? <div role="alert" className="tn-rq__err">{qError}</div> : null}
        </div>
      ) : null}

      {addons.length > 0 ? (
        <div className="tn-card tn-confirm__card">
          <div className="tn-confirm__label">Add-ons (optional)</div>
          <ul className="tn-addons">
            {addons.map((p) => {
              const qty = addonQuantities[p.id] ?? 0;
              const cap = Math.min(
                p.max_per_order ?? Infinity,
                p.quantity_remaining ?? Infinity,
              );
              const soldOut = (p.quantity_remaining ?? Infinity) <= 0;
              return (
                <li className="tn-addon" key={p.id}>
                  <div className="tn-addon__info">
                    <span className="tn-addon__name">{p.name}</span>
                    {p.description ? (
                      <span className="tn-addon__desc">{p.description}</span>
                    ) : null}
                    <span className="tn-addon__price">{money(p.price_cents)} each</span>
                  </div>
                  <div className="tn-addon__stepper">
                    <button
                      type="button"
                      className="tn-addon__step"
                      aria-label={`Remove one ${p.name}`}
                      disabled={qty <= 0}
                      onClick={() => setAddonQuantity(p.id, qty - 1)}
                    >
                      −
                    </button>
                    <span className="tn-addon__qty" aria-live="polite">{qty}</span>
                    <button
                      type="button"
                      className="tn-addon__step"
                      aria-label={`Add one ${p.name}`}
                      disabled={soldOut || qty >= cap}
                      onClick={() => setAddonQuantity(p.id, qty + 1)}
                    >
                      +
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {addonSubtotalCents > 0 ? (
            <div className="tn-addons__subtotal">
              Add-ons subtotal: <strong>{money(addonSubtotalCents)}</strong>
              <span className="tn-addons__note"> · processing fee and total shown at payment</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {upsell ? (
        <button
          type="button"
          className="tn-confirm__upsell"
          onClick={() => {
            startBulkFrom(event, selectedDivision, answers);
            navigate('/bulk/events');
          }}
          disabled={busy}
        >
          <span className="tn-confirm__upsell-head">Playing more events? Save {offerLabel(upsell)}</span>
          <span className="tn-confirm__upsell-sub">
            Register for {upsell.min_events} or more {organizer ? `${organizer} ` : ''}events together
            and each one is {offerLabel(upsell)} off.
          </span>
        </button>
      ) : null}

      {me?.account_status === 'stub' ? (
        <SaveTeamOption checked={createAccount} onChange={setCreateAccount} />
      ) : null}

      <div className={`tn-confirm__actions${offlineAvailable ? ' tn-confirm__actions--stacked' : ''}`}>
        {offlineAvailable ? (
          <>
            <button
              className="tn-btn tn-btn--primary"
              type="button"
              onClick={() => onSubmit('card')}
              disabled={busy}
            >
              {submitting === 'card' ? 'Working…' : 'Pay with card'}
            </button>
            <button
              className="tn-btn tn-btn--secondary"
              type="button"
              onClick={() => onSubmit('offline')}
              disabled={busy}
            >
              {submitting === 'offline' ? 'Working…' : 'Pay offline'}
            </button>
            <button
              className="tn-btn tn-btn--ghost"
              type="button"
              onClick={() => navigate(-1)}
              disabled={busy}
            >
              Back
            </button>
          </>
        ) : (
          <>
            <button
              className="tn-btn tn-btn--primary"
              type="button"
              onClick={() => onSubmit('card')}
              disabled={busy}
            >
              {busy ? 'Submitting…' : requiresApproval ? 'Submit for approval' : 'Continue to payment'}
            </button>
            <button
              className="tn-btn tn-btn--ghost"
              type="button"
              onClick={() => navigate(-1)}
              disabled={busy}
            >
              Back
            </button>
          </>
        )}
      </div>

      {error ? <div className="tn-confirm__error" role="alert">{error}</div> : null}
    </div>
  );
}

function checkAnswers(questions: RegistrationQuestion[], answers: Record<string, string>): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const q of questions) {
    const v = (answers[q.id] ?? '').trim();
    if (!v) {
      if (q.required) errs[q.id] = 'This question is required.';
      continue;
    }
    if (q.type === 'number') {
      const n = Number(v.replace(/,/g, ''));
      if (Number.isNaN(n)) errs[q.id] = 'Enter a number.';
      else if (q.min != null && n < q.min) errs[q.id] = q.max != null ? `Enter a number from ${q.min} to ${q.max}.` : `Enter ${q.min} or more.`;
      else if (q.max != null && n > q.max) errs[q.id] = q.min != null ? `Enter a number from ${q.min} to ${q.max}.` : `Enter ${q.max} or less.`;
    }
  }
  return errs;
}

/** One registration question. Also used by the bulk divisions page, where
 *  several events' questions share a screen: ``idPrefix`` keeps ids unique. */
function QuestionField({ q, value, error, onChange, idPrefix = '' }: {
  q: RegistrationQuestion;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  idPrefix?: string;
}) {
  const id = `tn-rq-${idPrefix}${q.id}`;
  return (
    <div className={`tn-rq__field${error ? ' tn-rq__field--error' : ''}`}>
      <label htmlFor={id} className="tn-rq__label">
        {q.label}
        {q.required ? <span className="tn-rq__req"> *</span> : <span className="tn-rq__opt"> (optional)</span>}
      </label>
      {q.help ? <p className="tn-rq__help">{q.help}</p> : null}
      {q.type === 'choice' ? (
        <div className="tn-rq__choices" role="radiogroup" aria-labelledby={id}>
          {q.options.map((o, i) => (
            <label key={o} className={`tn-rq__choice${value === o ? ' tn-rq__choice--on' : ''}`}>
              <input type="radio" id={i === 0 ? id : undefined} name={id} value={o} checked={value === o} onChange={() => onChange(o)} />
              {o}
            </label>
          ))}
        </div>
      ) : (
        <input
          id={id}
          className="tn-rq__input"
          inputMode={q.type === 'number' ? 'decimal' : undefined}
          value={value}
          maxLength={q.type === 'number' ? 20 : 1000}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error || undefined}
        />
      )}
      {error ? <div role="alert" className="tn-rq__err">{error}</div> : null}
    </div>
  );
}
